"use client";

import { useMemo, useState } from "react";
import { Link } from "@/i18n/navigation";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    Avancer,
    Curseur,
    Ecran,
    Liste,
    Montant,
    Question,
    Recommencer,
    Resultat,
    Segments,
    euros,
    remplir,
    useParcours,
} from "./Journey";
import { versementPossible } from "@/lib/tools/monthlyPayout";
import { expliquerVersementMensuel } from "@/lib/tools/explanations";
import {
    IndiceExplication,
    MontantExplicable,
    PanneauExplication,
    phraseAvecMontants,
    useExplications,
} from "./Explanation";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import { reprendre } from "@/lib/tools/dossier";

/**
 * Combien pouvez-vous vous verser ce mois-ci ?
 *
 * LE SOLDE DU COMPTE N'EST PAS CE QUE LE DIRIGEANT PEUT PRENDRE. L'écran part
 * de ce que le lecteur voit, son solde, et retire ce qui ne lui appartient
 * pas : la TVA encaissée, l'impôt sur les sociétés du bénéfice fait depuis le
 * début de l'exercice, les cotisations à venir, et la réserve qu'il veut
 * garder. Ce qui reste est un plafond pour le mois, cotisations de ce
 * versement comprises.
 *
 * DEUX PORTES FERMÉES, DEUX REFUS MOTIVÉS. À l'impôt sur le revenu, le
 * bénéfice est déjà à l'entrepreneur : la question ne se pose pas dans les
 * mêmes termes, et l'écran dit ce qu'il faut garder à la place. À la Cipav,
 * le barème de retraite n'est pas modélisé : pas de chiffre plutôt qu'un faux.
 *
 * Le calcul est dans lib/tools/monthlyPayout.js, sur les barèmes du
 * comparateur ; npm run check:monthly-payout le vérifie.
 */

const PHASES = {
    forme: 0,
    ir: 0,
    activite: 0,
    cipav: 0,
    solde: 1,
    benefice: 1,
    dejaVerse: 1,
    dejaPaye: 1,
    tva: 2,
    coussin: 2,
    resultat: 3,
};

const L = {
    fr: {
        phases: ["Société", "Chiffres", "Réserves"],
        retour: "Retour",
        forme: {
            titre: "Quelle est votre société ?",
            aide: "Vous ne vous payez pas de la même façon en EURL et en SASU, et vos cotisations ne partent pas au même moment.",
            choix: [
                {
                    cle: "eurl",
                    icone: "buildings-2-linear",
                    titre: "EURL à l'impôt sur les sociétés",
                    detail: "Vous êtes gérant, et vous cotisez comme travailleur indépendant.",
                },
                {
                    cle: "sasu",
                    icone: "user-id-linear",
                    titre: "SASU",
                    detail: "Vous êtes président, et vous vous versez un salaire.",
                },
                {
                    cle: "ir",
                    icone: "case-minimalistic-linear",
                    titre: "Entreprise individuelle, ou EURL à l'impôt sur le revenu",
                    detail: "Votre bénéfice est imposé directement à votre nom.",
                },
            ],
        },
        ir: {
            titre: "À l'impôt sur le revenu, votre bénéfice est déjà à vous",
            aide: "Il est imposé à votre nom, que vous le laissiez sur le compte ou non. Vous pouvez donc vous verser ce que vous voulez, quand vous voulez.",
            points: [
                "Ce qu'il faut garder de côté, ce sont vos cotisations et votre impôt sur le revenu, calculés sur tout votre bénéfice de l'année, pas sur ce que vous retirez.",
                "Si vous voulez laisser de l'argent dans votre activité sans être imposé dessus tout de suite, regardez ce que changerait l'impôt sur les sociétés.",
            ],
            lien: "Passer à l'impôt sur les sociétés",
        },
        activite: {
            titre: "Quelle est votre activité ?",
            aide: "Elle change une partie de vos cotisations de gérant.",
            choix: [
                {
                    cle: "services",
                    icone: "shop-linear",
                    titre: "Commerce ou prestations",
                    detail: "Vous vendez, ou vous rendez des services à des entreprises ou à des particuliers.",
                },
                {
                    cle: "artisan",
                    icone: "settings-minimalistic-linear",
                    titre: "Artisanat",
                    detail: "Vous êtes immatriculé comme artisan.",
                },
                {
                    cle: "bnc",
                    icone: "pen-new-square-linear",
                    titre: "Activité libérale",
                    detail: "Conseil, développement, design, formation, rédaction.",
                },
                {
                    cle: "cipav",
                    icone: "stethoscope-linear",
                    titre: "Profession réglementée",
                    detail: "Architecte, psychologue, ostéopathe : vous cotisez à la Cipav.",
                },
            ],
        },
        cipav: {
            titre: "Pour la Cipav, nous n'avons pas encore le calcul",
            aide: "À la Cipav, vous cotisez pour votre retraite selon un barème à part, que nous ne savons pas encore calculer au centime. Plutôt qu'un chiffre faux, nous préférons ne rien vous afficher.",
            points: [
                "Le raisonnement, lui, vaut aussi pour vous : retirez de votre solde la TVA à reverser, l'impôt sur les sociétés de l'année et vos cotisations à venir avant de vous verser quoi que ce soit.",
            ],
        },
        solde: {
            titre: "Combien y a-t-il sur le compte de votre société aujourd'hui ?",
            aide: "Le solde que vous voyez dans votre banque, tous comptes professionnels confondus.",
            libelle: "Solde du compte",
            suivant: "Continuer",
        },
        benefice: {
            titre: "Combien votre société a-t-elle gagné depuis le début de l'exercice ?",
            aide: "Votre chiffre d'affaires hors taxes depuis l'ouverture de l'exercice, le 1er janvier pour la plupart, moins vos dépenses. Sans compter ce que vous vous êtes versé ni vos propres cotisations.",
            libelle: "Bénéfice avant votre rémunération",
            suivant: "Continuer",
        },
        dejaVerse: {
            titre: "Combien vous êtes-vous déjà versé depuis le début de l'exercice ?",
            aideSasu: "Vos salaires nets avant impôt, avant le prélèvement à la source.",
            aideEurl: "Votre rémunération de gérant, tout ce que vous avez viré sur votre compte personnel.",
            libelle: "Déjà versé, net avant impôt",
            suivant: "Continuer",
        },
        dejaPaye: {
            titre: "Qu'avez-vous déjà payé pour cet exercice ?",
            aideEurl: "Votre espace sur urssaf.fr et votre espace professionnel sur impots.gouv.fr vous donnent ces deux montants.",
            aideSasu: "Les cotisations de vos salaires sont déjà parties avec chaque paie. Il ne reste que l'impôt sur les sociétés : votre espace professionnel sur impots.gouv.fr vous donne les acomptes déjà payés.",
            cotisations: "Cotisations déjà payées à l'Urssaf",
            acomptes: "Acomptes d'impôt sur les sociétés déjà payés",
            suivant: "Continuer",
        },
        tva: {
            titre: "Avez-vous de la TVA à reverser ?",
            aide: "La TVA encaissée sur vos factures, moins celle que vous avez payée sur vos achats, depuis votre dernière déclaration.",
            sans: "Je ne facture pas la TVA",
            avec: "Je facture la TVA",
            libelle: "TVA à reverser",
            suivant: "Continuer",
        },
        coussin: {
            titre: "Combien voulez-vous laisser sur le compte, en plus ?",
            aide: "Pour les imprévus et les dépenses à venir : la CFE de décembre, un fournisseur, un client qui paie en retard. Garder au moins un mois de dépenses est un bon repère.",
            libelle: "Votre réserve",
            suivant: "Voir ce que je peux me verser",
        },
        resultat: {
            titre: "Ce mois-ci",
            possible: "Vous pouvez vous verser jusqu'à {montant} ce mois-ci",
            detailSasu: "C'est votre salaire net avant impôt, soit {brut} de salaire brut. Les cotisations de ce salaire sont déjà mises de côté ci-dessous.",
            detailEurl: "C'est votre rémunération nette avant impôt. Les cotisations qu'elle entraîne sont déjà mises de côté ci-dessous.",
            rien: "Ce mois-ci, mieux vaut ne rien vous verser",
            rienDetail: "Même sans vous verser un euro, il manque {manque} sur le compte pour couvrir ce que votre société doit déjà et la réserve que vous voulez garder.",
            garde: "Ce que votre société garde de côté",
            lignes: {
                tva: "La TVA à reverser",
                impot: "L'impôt sur les sociétés de l'exercice, acomptes déjà payés déduits",
                cotisationsSasu: "Les cotisations de ce salaire, qui partent avec lui",
                cotisationsEurl: "Vos cotisations encore à payer sur tout ce que vous vous êtes versé cet exercice, ce mois compris",
                coussin: "Votre réserve",
            },
            impotSasu:
                "Sur ce salaire, votre société retiendra aussi le prélèvement à la source de votre impôt sur le revenu : ce qui arrivera sur votre compte sera un peu plus bas.",
            impotEurl:
                "Votre impôt sur le revenu, lui, est prélevé sur votre compte personnel, au taux que vous a donné l'administration : vous le payez avec ce que vous vous versez.",
            dividende:
                "Vous pensiez à un dividende ? Il faut pour cela un exercice terminé et ses comptes approuvés : le calcul ci-dessus ne porte que sur votre rémunération.",
            lienPartage: "Rémunération ou dividendes : trouver le bon partage",
            hypotheses: {
                titre: "Pour ce calcul, nous avons supposé que :",
                items: [
                    "l'impôt sur les sociétés porte sur le bénéfice fait depuis le début de l'exercice, à 15 % jusqu'à 42 500 € puis 25 % ;",
                    "vos cotisations suivent le barème de 2026, appliqué à tout ce que vous vous êtes versé depuis le début de l'exercice ;",
                    "votre réserve couvre vos autres dépenses à venir : fournisseurs, abonnements, CFE.",
                ],
                sasu: "la cotisation accidents du travail et la prévoyance ne sont pas comptées ;",
                eurl: "l'Urssaf vous réclamera plus tard ce que vos cotisations provisionnelles n'ont pas couvert, et nous le mettons de côté dès maintenant ;",
            },
            avertissement:
                "Ce chiffre ne vaut que pour ce mois-ci. Refaites le calcul avant chaque versement : votre solde et votre bénéfice auront bougé.",
            recommencer: "Refaire le calcul",
        },
    },
    en: {
        phases: ["Company", "Figures", "Reserves"],
        retour: "Back",
        forme: {
            titre: "What is your company?",
            aide: "You do not pay yourself the same way in an EURL and in a SASU, and your contributions do not go out at the same time.",
            choix: [
                {
                    cle: "eurl",
                    icone: "buildings-2-linear",
                    titre: "EURL under corporate tax",
                    detail: "You are the manager, and you pay contributions as a self-employed person.",
                },
                {
                    cle: "sasu",
                    icone: "user-id-linear",
                    titre: "SASU",
                    detail: "You are the president, and you pay yourself a salary.",
                },
                {
                    cle: "ir",
                    icone: "case-minimalistic-linear",
                    titre: "Sole trader, or EURL under income tax",
                    detail: "Your profit is taxed directly in your name.",
                },
            ],
        },
        ir: {
            titre: "Under income tax, your profit is already yours",
            aide: "It is taxed in your name, whether you leave it in the account or not. So you can pay yourself what you want, when you want.",
            points: [
                "What you need to set aside is your contributions and your income tax, calculated on all of the year's profit, not on what you withdraw.",
                "If you want to leave money in your business without being taxed on it straight away, see what corporate tax would change.",
            ],
            lien: "Switching to corporate tax",
        },
        activite: {
            titre: "What is your activity?",
            aide: "It changes part of your contributions as a manager.",
            choix: [
                {
                    cle: "services",
                    icone: "shop-linear",
                    titre: "Trade or services",
                    detail: "You sell goods, or provide services to businesses or individuals.",
                },
                {
                    cle: "artisan",
                    icone: "settings-minimalistic-linear",
                    titre: "Craft trade",
                    detail: "You are registered as a craftsperson.",
                },
                {
                    cle: "bnc",
                    icone: "pen-new-square-linear",
                    titre: "Liberal profession",
                    detail: "Consulting, development, design, training, writing.",
                },
                {
                    cle: "cipav",
                    icone: "stethoscope-linear",
                    titre: "Regulated profession",
                    detail: "Architect, psychologist, osteopath: you contribute to the Cipav.",
                },
            ],
        },
        cipav: {
            titre: "We do not have the Cipav calculation yet",
            aide: "With the Cipav, your pension contributions follow their own scale, which we cannot yet compute to the cent. Rather than show you a wrong figure, we prefer to show nothing.",
            points: [
                "The reasoning does apply to you: take the VAT to pay back, the year's corporate tax and your upcoming contributions out of your balance before paying yourself anything.",
            ],
        },
        solde: {
            titre: "How much is in your company's account today?",
            aide: "The balance you see at your bank, all business accounts together.",
            libelle: "Account balance",
            suivant: "Continue",
        },
        benefice: {
            titre: "How much has your company made since the start of the financial year?",
            aide: "Your turnover excluding VAT since the financial year opened, 1 January for most, minus your expenses. Not counting what you have paid yourself or your own contributions.",
            libelle: "Profit before your pay",
            suivant: "Continue",
        },
        dejaVerse: {
            titre: "How much have you already paid yourself since the start of the financial year?",
            aideSasu: "Your net salaries before income tax, before withholding.",
            aideEurl: "Your pay as a manager, everything you have transferred to your personal account.",
            libelle: "Already paid, net before income tax",
            suivant: "Continue",
        },
        dejaPaye: {
            titre: "What have you already paid for this financial year?",
            aideEurl: "Your account on urssaf.fr and your business account on impots.gouv.fr give you both amounts.",
            aideSasu: "The contributions on your salaries already went out with each payslip. Only corporate tax remains: your business account on impots.gouv.fr gives you the instalments already paid.",
            cotisations: "Contributions already paid to the Urssaf",
            acomptes: "Corporate tax instalments already paid",
            suivant: "Continue",
        },
        tva: {
            titre: "Do you have VAT to pay back?",
            aide: "The VAT collected on your invoices, minus what you paid on your purchases, since your last return.",
            sans: "I do not charge VAT",
            avec: "I charge VAT",
            libelle: "VAT to pay back",
            suivant: "Continue",
        },
        coussin: {
            titre: "How much do you want to leave in the account, on top?",
            aide: "For the unexpected and upcoming expenses: the December CFE, a supplier, a client who pays late. Keeping at least a month of expenses is a good rule of thumb.",
            libelle: "Your reserve",
            suivant: "See what I can pay myself",
        },
        resultat: {
            titre: "This month",
            possible: "You can pay yourself up to {montant} this month",
            detailSasu: "That is your net salary before income tax, or {brut} gross. The contributions on that salary are already set aside below.",
            detailEurl: "That is your net pay before income tax. The contributions it triggers are already set aside below.",
            rien: "This month, better not to pay yourself anything",
            rienDetail: "Even without paying yourself a single euro, the account is {manque} short of covering what your company already owes and the reserve you want to keep.",
            garde: "What your company sets aside",
            lignes: {
                tva: "The VAT to pay back",
                impot: "Corporate tax for the financial year, instalments already paid deducted",
                cotisationsSasu: "The contributions on this salary, which go out with it",
                cotisationsEurl: "Your contributions still to pay on everything you have paid yourself this financial year, this month included",
                coussin: "Your reserve",
            },
            impotSasu:
                "On this salary, your company will also withhold your income tax at source: what reaches your account will be a little lower.",
            impotEurl:
                "Your income tax is taken from your personal account, at the rate the tax office gave you: you pay it out of what you pay yourself.",
            dividende:
                "Thinking of a dividend? That needs a completed financial year with approved accounts: the calculation above only covers your pay.",
            lienPartage: "Salary or dividends: find the right split",
            hypotheses: {
                titre: "For this calculation, we assumed that:",
                items: [
                    "corporate tax falls on the profit made since the start of the financial year, at 15% up to €42,500 then 25%;",
                    "your contributions follow the 2026 scale, applied to everything you have paid yourself since the start of the financial year;",
                    "your reserve covers your other upcoming expenses: suppliers, subscriptions, CFE.",
                ],
                sasu: "the workplace accident contribution and provident cover are not counted;",
                eurl: "the Urssaf will later claim what your provisional contributions did not cover, and we set it aside right now;",
            },
            avertissement:
                "This figure only holds for this month. Run the calculation again before each payment: your balance and your profit will have moved.",
            recommencer: "Run the calculation again",
        },
    },
};

export default function CombienMeVerser({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const r = t.resultat;

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    // L'impôt sur le revenu et la CIPAV ne sont jamais des valeurs de l'outil :
    // ce sont des écrans de refus, où la reprise mène directement.
    const { valeurs: dossier } = useDossierOutil();
    const formeDossier = dossier.forme === "eurl" || dossier.forme === "sasu" ? dossier.forme : undefined;
    const activiteDossier = dossier.activite !== "cipav" ? dossier.activite : undefined;
    const [forme, setForme] = useState(formeDossier ?? null);
    const [activite, setActivite] = useState(activiteDossier ?? null);
    const [solde, setSolde] = useState(20000);
    const [benefice, setBenefice] = useState(dossier.benefice ?? 40000);
    const [dejaVerse, setDejaVerse] = useState(10000);
    const [cotisationsPayees, setCotisationsPayees] = useState(3000);
    const [acomptesIs, setAcomptesIs] = useState(0);
    const [factureTva, setFactureTva] = useState(true);
    const [tvaAReverser, setTvaAReverser] = useState(3000);
    const [coussin, setCoussin] = useState(2000);

    const { etape, communs, rang, aller, retour, recommencer, reduit } = useParcours(
        "forme",
        () => {
            setForme(null);
            setActivite(null);
            setSolde(20000);
            setBenefice(40000);
            setDejaVerse(10000);
            setCotisationsPayees(3000);
            setAcomptesIs(0);
            setFactureTva(true);
            setTvaAReverser(3000);
            setCoussin(2000);
        },
        PHASES,
        reprendre(
            dossier.forme === "ir"
                ? [{ etape: "forme", connu: true }, { etape: "ir" }]
                : formeDossier === "eurl" && dossier.activite === "cipav"
                  ? [
                        { etape: "forme", connu: true },
                        { etape: "activite", connu: true },
                        { etape: "cipav" },
                    ]
                  : [
                        { etape: "forme", connu: formeDossier !== undefined },
                        ...(formeDossier === "eurl"
                            ? [{ etape: "activite", connu: activiteDossier !== undefined }]
                            : []),
                        // Le solde du compte ne se déduit pas du dossier : le parcours
                        // s'y arrête, et le bénéfice repris se relit à l'écran suivant.
                        { etape: "solde", connu: false },
                        { etape: "resultat" },
                    ],
        ),
    );

    useSignalerDossier({ forme, activite }, etape === "resultat");

    const entree = useMemo(
        () => ({
            forme,
            activite: activite === "artisan" ? "services" : activite || "services",
            artisan: activite === "artisan",
            solde,
            tvaAReverser: factureTva ? tvaAReverser : 0,
            benefice,
            dejaVerse,
            cotisationsPayees,
            acomptesIs,
            coussin,
        }),
        [forme, activite, solde, factureTva, tvaAReverser, benefice, dejaVerse, cotisationsPayees, acomptesIs, coussin],
    );
    const calcul = useMemo(() => (etape === "resultat" ? versementPossible(entree) : null), [etape, entree]);
    // Chaque montant du résultat dit d'où il vient : ses étapes et ses sources.
    const ex = useMemo(() => (calcul ? expliquerVersementMensuel(entree, calcul, lang) : null), [calcul, entree, lang]);
    const { ouverte, basculer } = useExplications();

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };
    const sommes = (v) => euros(Math.round(v), lang);
    const lienOutil = (fr, en, libelle, type = "primary") => (
        <Link href={{ pathname: "/tools/[slug]", params: { slug: lang === "fr" ? fr : en } }}>
            <Button type={type} className="press-safe w-full">
                {libelle} →
            </Button>
        </Link>
    );

    if (etape === "forme") {
        return (
            <Question
                key="forme"
                {...communsEcran}
                phase={PHASES.forme}
                rang={rang}
                titre={t.forme.titre}
                aide={t.forme.aide}
                choix={t.forme.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        if (c.cle === "ir") {
                            aller("ir");
                            return;
                        }
                        setForme(c.cle);
                        aller(c.cle === "eurl" ? "activite" : "solde");
                    },
                }))}
            />
        );
    }

    if (etape === "ir" || etape === "cipav") {
        const texte = t[etape];
        return (
            <Ecran key={etape} {...communsEcran} phase={PHASES[etape]} rang={rang} titre={texte.titre} aide={texte.aide}>
                <Liste items={texte.points} />
                {etape === "ir" && (
                    <div className="mt-5">
                        {lienOutil("passer-a-l-impot-sur-les-societes", "switch-to-corporate-tax", t.ir.lien)}
                    </div>
                )}
                <Recommencer onClick={recommencer} libelle={r.recommencer} />
            </Ecran>
        );
    }

    if (etape === "activite") {
        return (
            <Question
                key="activite"
                {...communsEcran}
                phase={PHASES.activite}
                rang={rang}
                titre={t.activite.titre}
                aide={t.activite.aide}
                choix={t.activite.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        if (c.cle === "cipav") {
                            aller("cipav");
                            return;
                        }
                        setActivite(c.cle);
                        aller("solde");
                    },
                }))}
            />
        );
    }

    if (etape === "solde") {
        return (
            <Ecran key="solde" {...communsEcran} phase={PHASES.solde} rang={rang} titre={t.solde.titre} aide={t.solde.aide}>
                <Curseur
                    libelle={t.solde.libelle}
                    valeur={solde}
                    poser={setSolde}
                    minimum={0}
                    maximum={100000}
                    saisie
                    pas={500}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("benefice")}>{t.solde.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "benefice") {
        return (
            <Ecran key="benefice" {...communsEcran} phase={PHASES.benefice} rang={rang} titre={t.benefice.titre} aide={t.benefice.aide}>
                <Curseur
                    libelle={t.benefice.libelle}
                    valeur={benefice}
                    poser={setBenefice}
                    minimum={0}
                    maximum={200000}
                    saisie
                    pas={1000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("dejaVerse")}>{t.benefice.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "dejaVerse") {
        return (
            <Ecran
                key="dejaVerse"
                {...communsEcran}
                phase={PHASES.dejaVerse}
                rang={rang}
                titre={t.dejaVerse.titre}
                aide={forme === "sasu" ? t.dejaVerse.aideSasu : t.dejaVerse.aideEurl}
            >
                <Curseur
                    libelle={t.dejaVerse.libelle}
                    valeur={dejaVerse}
                    poser={setDejaVerse}
                    minimum={0}
                    maximum={150000}
                    saisie
                    pas={500}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("dejaPaye")}>{t.dejaVerse.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "dejaPaye") {
        return (
            <Ecran
                key="dejaPaye"
                {...communsEcran}
                phase={PHASES.dejaPaye}
                rang={rang}
                titre={t.dejaPaye.titre}
                aide={forme === "sasu" ? t.dejaPaye.aideSasu : t.dejaPaye.aideEurl}
            >
                <div className="space-y-5">
                    {forme === "eurl" && (
                        <Curseur
                            libelle={t.dejaPaye.cotisations}
                            valeur={cotisationsPayees}
                            poser={setCotisationsPayees}
                            minimum={0}
                            maximum={60000}
                            saisie
                            pas={100}
                            formater={sommes}
                        />
                    )}
                    <Curseur
                        libelle={t.dejaPaye.acomptes}
                        valeur={acomptesIs}
                        poser={setAcomptesIs}
                        minimum={0}
                        maximum={50000}
                        saisie
                        pas={100}
                        formater={sommes}
                    />
                </div>
                <Avancer onClick={() => aller("tva")}>{t.dejaPaye.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "tva") {
        return (
            <Ecran key="tva" {...communsEcran} phase={PHASES.tva} rang={rang} titre={t.tva.titre} aide={t.tva.aide}>
                <div className="space-y-5">
                    <Segments
                        options={[
                            { cle: "avec", libelle: t.tva.avec },
                            { cle: "sans", libelle: t.tva.sans },
                        ]}
                        valeur={factureTva ? "avec" : "sans"}
                        poser={(v) => setFactureTva(v === "avec")}
                    />
                    {factureTva && (
                        <Curseur
                            libelle={t.tva.libelle}
                            valeur={tvaAReverser}
                            poser={setTvaAReverser}
                            minimum={0}
                            maximum={40000}
                            saisie
                            pas={100}
                            formater={sommes}
                        />
                    )}
                </div>
                <Avancer onClick={() => aller("coussin")}>{t.tva.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "coussin") {
        return (
            <Ecran key="coussin" {...communsEcran} phase={PHASES.coussin} rang={rang} titre={t.coussin.titre} aide={t.coussin.aide}>
                <Curseur
                    libelle={t.coussin.libelle}
                    valeur={coussin}
                    poser={setCoussin}
                    minimum={0}
                    maximum={30000}
                    saisie
                    pas={500}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("resultat")}>{t.coussin.suivant}</Avancer>
            </Ecran>
        );
    }

    const { versement, brut, tva, impot, cotisations, manque } = calcul;
    const peut = versement >= 1;
    const lignes = [
        { cle: "tva", libelle: r.lignes.tva, montant: tva, visible: tva > 0 },
        { cle: "impot", libelle: r.lignes.impot, montant: impot, visible: true },
        {
            cle: "cotisations",
            libelle: forme === "sasu" ? r.lignes.cotisationsSasu : r.lignes.cotisationsEurl,
            montant: cotisations,
            visible: true,
        },
        { cle: "coussin", libelle: r.lignes.coussin, montant: coussin, visible: coussin > 0 },
    ].filter((l) => l.visible);

    return (
        <Resultat onRetour={retour} retour={t.retour} key="resultat" titre={r.titre} phases={t.phases} {...communs}>
            {peut ? (
                <>
                    <div className="mt-2 font-bold text-2xl leading-tight dark:text-white">
                        {phraseAvecMontants(r.possible, {
                            montant: (
                                <MontantExplicable
                                    explication={ex.versement}
                                    ouvert={ouverte === "versement"}
                                    onBasculer={() => basculer("versement")}
                                >
                                    {sommes(versement)}
                                </MontantExplicable>
                            ),
                        })}
                    </div>
                    {ouverte === "versement" && <PanneauExplication explication={ex.versement} lang={lang} />}
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {forme === "sasu" ? remplir(r.detailSasu, { brut: sommes(brut) }) : r.detailEurl}
                    </p>
                </>
            ) : (
                <>
                    <div className="mt-2 font-bold text-2xl leading-tight dark:text-white">{r.rien}</div>
                    {manque > 0 && (
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                            {remplir(r.rienDetail, { manque: sommes(manque) })}
                        </p>
                    )}
                </>
            )}

            <div className="mt-6 rounded-xl border border-gray-200/70 dark:border-white/10 px-4 py-4">
                <div className="flex items-center gap-2 font-semibold dark:text-white">
                    <SolarIcon name="safe-square-linear" className="w-5 h-5 text-gray-400 dark:text-lightBlue/60" />
                    {r.garde}
                </div>
                <ul className="mt-3 space-y-2.5">
                    {lignes.map((l) => (
                        <li key={l.cle} className="text-sm">
                            <div className="flex items-baseline justify-between gap-4">
                                <span className="text-gray-600 dark:text-lightBlue">{l.libelle}</span>
                                <span className="font-semibold tabular-nums dark:text-white shrink-0">
                                    <MontantExplicable
                                        explication={ex[l.cle]}
                                        ouvert={ouverte === l.cle}
                                        onBasculer={() => basculer(l.cle)}
                                    >
                                        <Montant valeur={Math.round(l.montant)} lang={lang} reduit={reduit} />
                                    </MontantExplicable>
                                </span>
                            </div>
                            {ouverte === l.cle && <PanneauExplication explication={ex[l.cle]} lang={lang} />}
                        </li>
                    ))}
                </ul>
                <IndiceExplication lang={lang} />
            </div>

            <p className="text-sm text-gray-600 dark:text-lightBlue mt-5 mb-0">
                {forme === "sasu" ? r.impotSasu : r.impotEurl}
            </p>
            <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">{r.dividende}</p>

            <div className="mt-6">
                {lienOutil("remuneration-ou-dividendes", "salary-or-dividends", r.lienPartage, "secondary")}
            </div>

            <div className="mt-6">
                <div className="font-semibold dark:text-white">{r.hypotheses.titre}</div>
                <Liste
                    items={[
                        ...r.hypotheses.items.slice(0, 2),
                        forme === "sasu" ? r.hypotheses.sasu : r.hypotheses.eurl,
                        r.hypotheses.items[2],
                    ]}
                />
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-6 mb-0 leading-relaxed">{r.avertissement}</p>

            <Recommencer onClick={recommencer} libelle={r.recommencer} />
        </Resultat>
    );
}
